/**
 * BusinessRules.jsx (v1.3.1)
 * Per-user inspection business rules configuration — sub-type level granularity.
 * Accordion layout: each spec category expands to reveal its sub-types.
 * Each sub-type row has tool checkboxes, sample size, tolerance source, and notes.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

// ── Toast ─────────────────────────────────────────────────────────────────────

function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((msg, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);
  return { toasts, add };
}

function ToastContainer({ toasts }) {
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          background: t.type === 'error' ? '#dc2626' : '#16a34a',
          color: '#fff', padding: '10px 16px', borderRadius: 8,
          fontSize: 13, fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,.25)',
          maxWidth: 320,
        }}>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

// ── Tolerance source options ──────────────────────────────────────────────────

const TOLERANCE_OPTS = [
  { value: 'drawing',         label: 'From Drawing' },
  { value: 'block_tolerance', label: 'Block Tolerance' },
  { value: 'custom',          label: 'Custom Value' },
];

const SAMPLE_OPTS = [
  '100%', 'AQL 1.0 (Level I)', 'AQL 1.5 (Level II)', 'AQL 2.5 (Level II)',
  'AQL 4.0 (Level II)', '10%', '25%', '50%', 'First Article Only', 'Custom',
];

// ── Skeleton loading (initial page load) ────────────────────────────────────

const BR_SKEL_SHIMMER = `
@keyframes br-skel-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
`;

function BrSkel({ w = '100%', h = 14, r = 6, style = {} }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: w,
        height: h,
        borderRadius: r,
        background: 'linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 22%, #f8fafc 44%, #e2e8f0 66%, #f1f5f9 100%)',
        backgroundSize: '200% 100%',
        animation: 'br-skel-shimmer 1.35s ease-in-out infinite',
        ...style,
      }}
    />
  );
}

function BusinessRulesSkeleton() {
  const accordionBlocks = [0, 1, 2, 3, 4, 5];
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading business rules"
      style={{ padding: '32px 40px', maxWidth: 900, margin: '0 auto' }}
    >
      <style>{BR_SKEL_SHIMMER}</style>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <BrSkel w={240} h={26} r={8} style={{ marginBottom: 12 }} />
        <BrSkel w="92%" h={15} r={6} style={{ marginBottom: 8 }} />
        <BrSkel w="68%" h={15} r={6} />
      </div>

      {/* Custom tools card */}
      <div style={{
        border: '1.5px solid #e5e7eb',
        borderRadius: 12,
        background: '#fafafa',
        padding: '20px 24px',
        marginBottom: 28,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <BrSkel w={28} h={28} r={8} />
          <BrSkel w={200} h={16} r={6} />
        </div>
        <BrSkel w="100%" h={13} r={5} style={{ marginBottom: 8 }} />
        <BrSkel w="88%" h={13} r={5} style={{ marginBottom: 16 }} />
        <div style={{ display: 'flex', gap: 8 }}>
          <BrSkel w="100%" h={40} r={8} style={{ flex: 1 }} />
          <BrSkel w={120} h={40} r={8} />
        </div>
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <BrSkel w={72} h={34} r={8} />
        <BrSkel w={110} h={34} r={8} />
        <BrSkel w={140} h={34} r={8} />
      </div>

      {/* Category accordion placeholders */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {accordionBlocks.map(i => (
          <div
            key={i}
            style={{
              border: '1.5px solid #e5e7eb',
              borderRadius: 10,
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px' }}>
              <BrSkel w={14} h={14} r={3} />
              <BrSkel h={16} r={6} style={{ flex: 1 }} />
              <BrSkel w={100} h={22} r={12} />
              <BrSkel w={72} h={22} r={12} />
            </div>
            <div style={{ padding: '12px 20px 16px', borderTop: '1px solid #f1f5f9', background: '#fafafa' }}>
              <BrSkel w="40%" h={12} r={4} style={{ marginBottom: 10 }} />
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <BrSkel h={52} r={8} style={{ flex: 1 }} />
                <BrSkel h={52} r={8} style={{ flex: 1 }} />
              </div>
              <BrSkel w="100%" h={36} r={7} style={{ marginBottom: 10 }} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <BrSkel h={36} r={7} />
                <BrSkel h={36} r={7} />
                <BrSkel h={36} r={7} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Sub-type row ──────────────────────────────────────────────────────────────

function SubTypeRow({ catKey, specType, subType, toolsOptions, customTools, savedRule, onSaved, onCleared, toast }) {
  const isConfigured = !!savedRule;

  const initState = () => {
    if (savedRule) {
      return {
        tool_mode:        savedRule.tool_mode,
        tools_list:       savedRule.tools_list ?? [],
        sample_size:      savedRule.sample_size ?? '',
        tolerance_source: savedRule.tolerance_source ?? 'drawing',
        custom_tolerance: savedRule.custom_tolerance ?? '',
        notes:            savedRule.notes ?? '',
      };
    }
    // No saved rule — default to "fixed" with CMM pre-checked (if available in this category)
    const defaultTool = toolsOptions.includes('CMM') ? ['CMM'] : (toolsOptions.length ? [toolsOptions[0]] : []);
    return {
      tool_mode:        'fixed',
      tools_list:       defaultTool,
      sample_size:      '',
      tolerance_source: 'drawing',
      custom_tolerance: '',
      notes:            '',
    };
  };

  const [form, setForm]       = useState(initState);
  const [saving, setSaving]   = useState(false);
  const [clearing, setClearing] = useState(false);
  const [dirty, setDirty]     = useState(false);
  const prevSaved             = useRef(savedRule);

  useEffect(() => {
    if (prevSaved.current !== savedRule) {
      prevSaved.current = savedRule;
      setForm(initState());
      setDirty(false);
    }
  }, [savedRule]);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const toggleTool = (tool) => {
    setForm(prev => {
      const list = prev.tools_list.includes(tool)
        ? prev.tools_list.filter(t => t !== tool)
        : [...prev.tools_list, tool];
      return { ...prev, tools_list: list };
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (form.tool_mode === 'fixed' && !form.tools_list.length && !form.sample_size && !form.notes) {
      toast.add('Select at least one tool or fill a field before saving.', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        spec_category:    catKey,
        spec_sub_type:    subType,
        spec_type:        specType,
        tool_mode:        form.tool_mode,
        tools_list:       form.tool_mode === 'fixed' ? form.tools_list : [],
        sample_size:      form.sample_size || null,
        tolerance_source: form.tolerance_source,
        custom_tolerance: form.tolerance_source === 'custom' ? (form.custom_tolerance || null) : null,
        notes:            form.notes || null,
      };
      const saved = await api.upsertBusinessRule(payload);
      onSaved(catKey, subType, saved);
      setDirty(false);
      toast.add(`Rule saved for "${subType}"`);
    } catch (err) {
      toast.add(err.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await api.deleteBusinessRule(catKey, subType);
      onCleared(catKey, subType);
      const defaultTool = toolsOptions.includes('CMM') ? ['CMM'] : (toolsOptions.length ? [toolsOptions[0]] : []);
      setForm({ tool_mode: 'fixed', tools_list: defaultTool, sample_size: '', tolerance_source: 'drawing', custom_tolerance: '', notes: '' });
      setDirty(false);
      toast.add(`Rule cleared for "${subType}"`);
    } catch (err) {
      toast.add(err.message || 'Failed to clear', 'error');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div style={{
      borderTop: '1px solid #e5e7eb',
      padding: '14px 20px',
      background: isConfigured ? 'rgba(16,185,129,.04)' : 'transparent',
    }}>
      {/* Sub-type header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', flex: 1 }}>{subType}</span>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
          background: isConfigured ? '#d1fae5' : '#f1f5f9',
          color: isConfigured ? '#065f46' : '#64748b',
        }}>
          {isConfigured ? 'Configured' : 'Default'}
        </span>
      </div>

      {/* Tool mode toggle */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: .5 }}>
          Tool Selection Mode
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {[
            { value: 'model_generated', label: 'Model Generated', desc: 'The model selects the best tool at scan time' },
            { value: 'fixed',           label: 'Pick a specific tool', desc: 'You choose the tool below' },
          ].map(opt => {
            const active = form.tool_mode === opt.value;
            return (
              <label key={opt.value} style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer',
                padding: '8px 12px', borderRadius: 8,
                border: `1.5px solid ${active ? '#6366f1' : '#e5e7eb'}`,
                background: active ? 'rgba(99,102,241,.06)' : '#fff',
                flex: 1, transition: 'all .15s',
              }}>
                <input
                  type="radio"
                  name={`tool_mode_${catKey}_${subType}`}
                  value={opt.value}
                  checked={active}
                  onChange={() => update('tool_mode', opt.value)}
                  style={{ accentColor: '#6366f1', marginTop: 2, cursor: 'pointer', flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: active ? '#4f46e5' : '#374151' }}>
                    {opt.label}
                    {opt.value === 'fixed' && (
                      <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: '#d1fae5', color: '#065f46', textTransform: 'uppercase', letterSpacing: .4 }}>
                        Default
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{opt.desc}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Tool checkboxes — only shown when tool_mode is "fixed" */}
      {form.tool_mode === 'fixed' && (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: .5 }}>
          Measurement Tools
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {toolsOptions.map(tool => {
            const checked = form.tools_list.includes(tool);
            return (
              <label key={tool} style={{
                display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                fontSize: 12, color: '#374151', userSelect: 'none',
                padding: '4px 10px', borderRadius: 6, border: `1.5px solid ${checked ? '#10b981' : '#e5e7eb'}`,
                background: checked ? '#d1fae5' : '#fff', transition: 'all .15s',
              }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleTool(tool)}
                  style={{ accentColor: '#10b981', cursor: 'pointer' }}
                />
                {tool}
              </label>
            );
          })}
          {/* Custom tools */}
          {customTools && customTools.map(ct => {
            const checked = form.tools_list.includes(ct.tool_name);
            return (
              <label key={`custom-${ct.id}`} style={{
                display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                fontSize: 12, color: '#374151', userSelect: 'none',
                padding: '4px 10px', borderRadius: 6,
                border: `1.5px solid ${checked ? '#f97316' : '#fed7aa'}`,
                background: checked ? '#fff7ed' : '#fff', transition: 'all .15s',
              }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleTool(ct.tool_name)}
                  style={{ accentColor: '#f97316', cursor: 'pointer' }}
                />
                {ct.tool_name}
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                  background: '#fed7aa', color: '#c2410c', textTransform: 'uppercase', letterSpacing: .4,
                }}>custom</span>
              </label>
            );
          })}
        </div>
      </div>
      )}

      {/* Bottom row: sample size + tolerance + notes */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        {/* Sample size */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: .5 }}>
            Sample Size / Frequency
          </label>
          <select
            value={form.sample_size}
            onChange={e => update('sample_size', e.target.value)}
            style={selectStyle}
          >
            <option value="">— System default —</option>
            {SAMPLE_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        {/* Tolerance source */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: .5 }}>
            Tolerance Source
          </label>
          <select
            value={form.tolerance_source}
            onChange={e => update('tolerance_source', e.target.value)}
            style={selectStyle}
          >
            {TOLERANCE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Custom tolerance (conditional) */}
        {form.tolerance_source === 'custom' && (
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: .5 }}>
              Custom Tolerance Value
            </label>
            <input
              type="text"
              placeholder="e.g. ±0.05 mm"
              value={form.custom_tolerance}
              onChange={e => update('custom_tolerance', e.target.value)}
              style={inputStyle}
            />
          </div>
        )}
      </div>

      {/* Notes */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: .5 }}>
          Notes / Additional Instructions
        </label>
        <input
          type="text"
          placeholder="e.g. Inspect before and after assembly"
          value={form.notes}
          onChange={e => update('notes', e.target.value)}
          style={{ ...inputStyle, width: '100%' }}
        />
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {isConfigured && (
          <button
            onClick={handleClear}
            disabled={clearing}
            style={{ ...btnStyle, background: '#fff', color: '#dc2626', border: '1.5px solid #dc2626' }}
          >
            {clearing ? 'Clearing…' : 'Clear Rule'}
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={saving || (!dirty && isConfigured)}
          style={{ ...btnStyle, background: saving || (!dirty && isConfigured) ? '#9ca3af' : '#10b981', color: '#fff' }}
        >
          {saving ? 'Saving…' : isConfigured ? (dirty ? 'Update Rule' : 'Saved ✓') : 'Save Rule'}
        </button>
      </div>
    </div>
  );
}

// ── Category accordion ────────────────────────────────────────────────────────

function CategoryAccordion({ cat, rulesMap, onSaved, onCleared, toast, defaultOpen, customTools }) {
  const [open, setOpen] = useState(defaultOpen || false);

  const configuredCount = cat.sub_types.filter(
    st => rulesMap[`${cat.key}:${st}`],
  ).length;

  return (
    <div style={{
      border: '1.5px solid #e5e7eb',
      borderRadius: 10,
      overflow: 'hidden',
      background: '#fff',
    }}>
      {/* Header */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 14, color: '#64748b', transition: 'transform .2s', display: 'inline-block', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        <span style={{ flex: 1, fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{cat.display_label}</span>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 12,
          background: cat.spec_type === 'dimensional' ? '#eff6ff' : '#fdf4ff',
          color: cat.spec_type === 'dimensional' ? '#1d4ed8' : '#7c3aed',
        }}>
          {cat.spec_type === 'dimensional' ? 'Dimensional' : 'Non-Dimensional'}
        </span>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 12,
          background: configuredCount > 0 ? '#d1fae5' : '#f1f5f9',
          color: configuredCount > 0 ? '#065f46' : '#94a3b8',
        }}>
          {configuredCount}/{cat.sub_types.length} configured
        </span>
      </button>

      {/* Sub-type rows */}
      {open && cat.sub_types.map(st => (
        <SubTypeRow
          key={st}
          catKey={cat.key}
          specType={cat.spec_type}
          subType={st}
          toolsOptions={cat.tools_options}
          customTools={customTools}
          savedRule={rulesMap[`${cat.key}:${st}`] || null}
          onSaved={onSaved}
          onCleared={onCleared}
          toast={toast}
        />
      ))}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const selectStyle = {
  width: '100%', padding: '7px 10px', border: '1.5px solid #e5e7eb',
  borderRadius: 7, fontSize: 13, color: '#1e293b', background: '#fff', outline: 'none',
};

const inputStyle = {
  padding: '7px 10px', border: '1.5px solid #e5e7eb',
  borderRadius: 7, fontSize: 13, color: '#1e293b', background: '#fff', outline: 'none',
};

const btnStyle = {
  padding: '7px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', border: 'none', transition: 'opacity .15s',
};

// ── Main page ─────────────────────────────────────────────────────────────────

export default function BusinessRules() {
  const [categories,    setCategories]    = useState([]);
  const [rulesMap,      setRulesMap]      = useState({});
  const [loading,       setLoading]       = useState(true);
  const [filter,        setFilter]        = useState('all');
  const [customTools,   setCustomTools]   = useState([]);
  const [newToolName,   setNewToolName]   = useState('');
  const [addingTool,    setAddingTool]    = useState(false);
  const [deletingTool,  setDeletingTool]  = useState(null);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const [cats, rules, tools] = await Promise.all([
          api.getBusinessRulesCategories(),
          api.getBusinessRules(),
          api.getCustomTools(),
        ]);
        setCategories(cats);
        const map = {};
        rules.forEach(r => {
          if (r.spec_sub_type) {
            map[`${r.spec_category}:${r.spec_sub_type}`] = r;
          }
        });
        setRulesMap(map);
        setCustomTools(tools);
      } catch (err) {
        toast.add(err.message || 'Failed to load business rules', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleAddTool = async () => {
    const name = newToolName.trim();
    if (!name) return;
    setAddingTool(true);
    try {
      const tool = await api.addCustomTool(name);
      setCustomTools(prev => [tool, ...prev]);
      setNewToolName('');
      toast.add(`Custom tool "${tool.tool_name}" added`);
    } catch (err) {
      toast.add(err.message || 'Failed to add tool', 'error');
    } finally {
      setAddingTool(false);
    }
  };

  const handleDeleteTool = async (id, name) => {
    setDeletingTool(id);
    try {
      await api.deleteCustomTool(id);
      setCustomTools(prev => prev.filter(t => t.id !== id));
      toast.add(`Tool "${name}" removed`);
    } catch (err) {
      toast.add(err.message || 'Failed to delete tool', 'error');
    } finally {
      setDeletingTool(null);
    }
  };

  const handleSaved = useCallback((catKey, subType, savedRule) => {
    setRulesMap(prev => ({ ...prev, [`${catKey}:${subType}`]: savedRule }));
  }, []);

  const handleCleared = useCallback((catKey, subType) => {
    setRulesMap(prev => {
      const next = { ...prev };
      delete next[`${catKey}:${subType}`];
      return next;
    });
  }, []);

  const visibleCats = categories.filter(c =>
    filter === 'all' || c.spec_type === filter,
  );

  const totalConfigured = Object.keys(rulesMap).length;

  if (loading) {
    return <BusinessRulesSkeleton />;
  }

  return (
    <div style={{ padding: '32px 40px', maxWidth: 900, margin: '0 auto' }}>
      <ToastContainer toasts={toast.toasts} />

      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>Business Rules</h1>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
          Define your organisation's inspection rules per specification sub-type.
          The AI will apply these rules automatically during every scan.
          {totalConfigured > 0 && (
            <span style={{ marginLeft: 8, fontWeight: 600, color: '#10b981' }}>
              {totalConfigured} sub-type rule{totalConfigured !== 1 ? 's' : ''} configured.
            </span>
          )}
        </p>
      </div>

      {/* Custom Tools card */}
      <div style={{
        border: '1.5px solid #fed7aa', borderRadius: 12, background: '#fffbf7',
        padding: '20px 24px', marginBottom: 28,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 16 }}>🔧</span>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#7c2d12' }}>Custom Measurement Tools</h2>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
          Add your own measurement tool names. They will be available in every sub-type rule and in the edit mode of the inspection plan output.
        </p>

        {/* Add tool input row */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input
            type="text"
            value={newToolName}
            onChange={e => setNewToolName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddTool()}
            placeholder="e.g. Laser Tracker, Optical Comparator, SmartScope…"
            maxLength={120}
            style={{
              flex: 1, padding: '8px 12px', border: '1.5px solid #fed7aa',
              borderRadius: 8, fontSize: 13, color: '#1e293b', background: '#fff', outline: 'none',
            }}
          />
          <button
            onClick={handleAddTool}
            disabled={addingTool || !newToolName.trim()}
            style={{
              padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700,
              cursor: addingTool || !newToolName.trim() ? 'not-allowed' : 'pointer',
              border: 'none', background: '#f97316', color: '#fff', opacity: addingTool || !newToolName.trim() ? .55 : 1,
              transition: 'opacity .15s',
            }}
          >
            {addingTool ? 'Adding…' : '+ Add Tool'}
          </button>
        </div>

        {/* Tool chips */}
        {customTools.length === 0 ? (
          <p style={{ margin: 0, fontSize: 12, color: '#d97706', fontStyle: 'italic' }}>
            No custom tools added yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {customTools.map(ct => (
              <span key={ct.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: '#fed7aa', color: '#7c2d12', border: '1px solid #fb923c',
              }}>
                {ct.tool_name}
                <button
                  onClick={() => handleDeleteTool(ct.id, ct.tool_name)}
                  disabled={deletingTool === ct.id}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#c2410c', fontWeight: 800, fontSize: 13, lineHeight: 1,
                    padding: 0, opacity: deletingTool === ct.id ? .4 : 1,
                  }}
                  title="Remove tool"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Filter toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { key: 'all',             label: 'All' },
          { key: 'dimensional',     label: 'Dimensional' },
          { key: 'non_dimensional', label: 'Non-Dimensional' },
        ].map(opt => (
          <button
            key={opt.key}
            onClick={() => setFilter(opt.key)}
            style={{
              padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', border: '1.5px solid',
              borderColor: filter === opt.key ? '#10b981' : '#e5e7eb',
              background:  filter === opt.key ? '#d1fae5' : '#fff',
              color:       filter === opt.key ? '#065f46' : '#374151',
              transition: 'all .15s',
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Category accordions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {visibleCats.map(cat => (
          <CategoryAccordion
            key={cat.key}
            cat={cat}
            rulesMap={rulesMap}
            onSaved={handleSaved}
            onCleared={handleCleared}
            toast={toast}
            customTools={customTools}
          />
        ))}
      </div>

      {/* Empty state */}
      {visibleCats.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 14 }}>
          No categories match the selected filter.
        </div>
      )}
    </div>
  );
}
